import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Container,
  Backdrop,
  CircularProgress,
  Typography,
} from "@mui/material";
import PortfolioRebalanceHeader from "../Component/PortfolioRebalance/PortfolioRebalanceHeader";
import PortfolioRebalanceTable from "../Component/PortfolioRebalance/PortfolioRebalanceTable";
import BondDetailsModal from "../Component/BondDetails/BondDetailsModal";
import AccountDeletedModal from "../Component/AccountPositions/AccountDeletedModal";
import { usePortfolioRebalanceStore } from "../Store/portfolioRebalanceStore";
import { useAuth } from "../Context/AuthProvider";
import { fetchSecurityInfo } from "../http/api";
import useSnackbar from "../Utils/useSnackbar";
import { usePortfolioRebalanceWebSocketUpdates } from "../Component/PortfolioRebalance/usePortfolioRebalanceWebSocketUpdates";
import CustomizeColumns from "../Component/CustomizeColumns";
import { clearConfigIfOutdated, updateColumnConfigIfNeeded } from "../Utils/columnVersionManager";
import { getPreference, setPreference } from "../Utils/userPreferenceManger";
import { tokens, withAlpha } from '../Common/designTokens'; // added
import { usePageSubscription } from '../Utils/WebSocketProvider.jsx';
import { useOpenReadyResults } from "../Component/PortfolioRebalance/PortfolioRebalanceJobWatcher";
import InformativeModal from "../Component/PortfolioRebalance/InformativeModal";

const PortfolioRebalanceScreen = () => {
  const { reset } = usePortfolioRebalanceStore();
  const { token, userId } = useAuth(); // Add userId from useAuth
  const { showError, SnackbarComponent } = useSnackbar();
  usePageSubscription("accountPositions");

  // Initialize WebSocket updates
  usePortfolioRebalanceWebSocketUpdates();

  const {
    positions,
    pagination,
    error,
    selectedAccount,
    selectedPositionsData,
    selectedBondsTypeCount,
    fetchAccountPositions,
    selectedBondType,
    selectedSecurityCharge,
    fetchBondTypeOptions,
    fetchAllPositionsForValidation,
    pendingInfoModal,
    clearPendingInfoModal,
  } = usePortfolioRebalanceStore();

  // Surfaces ready async results by routing to the workbench (never auto-opens
  // Order Preview, regardless of how many are ready). The user opens a specific
  // account via its inline "View Results" / the dropdown dots.
  const openReadyResults = useOpenReadyResults();

  // Local state for position loading
  const [isLoadingPositions, setIsLoadingPositions] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Sort configuration
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // Bond details modal state
  const [bondDetailsOpen, setBondDetailsOpen] = useState(false);
  const [selectedBondDetails, setSelectedBondDetails] = useState(null);
  const [bondDetailsLoading, setBondDetailsLoading] = useState(false);

  // Account deleted modal state
  const [accountDeletedModalOpen, setAccountDeletedModalOpen] = useState(false);
  const [deletedAccountInfo, setDeletedAccountInfo] = useState(null);

  // State for column customization
  const [ccOpen, setCcOpen] = useState(false);
  const customizeButtonRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Track last shown error
  const lastShownError = useRef(null);

  // Define all available columns
  const allColumns = [
    { key: "cusip", name: "SecurityId" },
    { key: "securityDesc", name: "Security Description" }, // changed from description
    {
      key: "quantity",
      name: "Qty",
      numeric: true,
    },
    {
      key: "securityWeightPercentage",
      name: "Weight %",
      numeric: true,
    },
    {
      key: "securityMarketValue",
      name: "Market Value",
      numeric: true,
    },
    {
      key: "price",
      name: "Price",
      numeric: true,
    },
    {
      key: "bondType",
      name: "Bond Type",
      render: (value) => value || "---",
    },
    {
      key: "securityCharge",
      name: "Factored Securities",
      render: (value) => value || "---",
    },
    {
      key: "maturityDate", // changed from maturity
      name: "Maturity Date",
    },
    {
      key: "moodyRating",
      name: "Moody's Rating",
      render: (value) => value || "---",
    },
    {
      key: "spRating", // changed from ratingSnp
      name: "S&P Rating",
      render: (value) => value || "---",
    },
    {
      key: "couponPer", // changed from coupon
      name: "Coupon",
      render: (value) => value ?? "---",
    },
    {
      key: "yield",
      name: "YTM",
      numeric: true,
    },
    {
      key: "yieldToWorst",
      name: "YTW",
      numeric: true,
    },
    {
      key: "accruedInterest",
      name: "Accrued Interest",
      numeric: true,
    },
    {
      key: "dv01",
      name: "DV01",
      numeric: true,
    },
    {
      key: "duration",
      name: "$ Duration",
      numeric: true,
    },
    {
      key: "modifiedConvexity",
      name: "Convexity",
      numeric: true,
    },
    {
      key: "isCallable", // changed from callable
      name: "Callable",
    },
    {
      key: "callStartDate",
      name: "Call Date",
    },
    {
      key: "nextCallDate",
      name: "Next Call Date",
    },
    {
      key: "callEndDate",
      name: "Call End Date",
    },
  ];

  // Selected columns state for customize columns (mimic Account Positions)
  const [selectedColumns, setSelectedColumns] = useState(() => {
    clearConfigIfOutdated("portfolio-rebalance", allColumns);
    // updateColumnConfigIfNeeded("account-positions", allColumns);

    const saved = getPreference("selectedColumns_portfolio-rebalance");
    return saved || allColumns;
  });

  // Handler for customize columns button
  const handleCustomizeClick = (anchorEl) => {
    setAnchorEl(anchorEl);
    setCcOpen(true);
  };

  // Save selectedColumns to user preferences when changed
  useEffect(() => {
    setPreference("selectedColumns_portfolio-rebalance", selectedColumns);
  }, [selectedColumns]);

  // Format monetary values
  const formatMonetary = (value) => {
    if (value === undefined || value === null) return "---";
    return `$${Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };
  // Add initial loading state for all API calls
  const [initialLoading, setInitialLoading] = useState(true);

  // On entering the workbench we no longer auto-open a preview — the user opens a
  // ready account via its inline "View Results" / the dropdown green dots. (Kept
  // as the single routing entry point; a no-op navigation while already here.)
  useEffect(() => {
    openReadyResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move initial API loading logic here
  useEffect(() => {
    let cancelled = false;
    if (token && userId) { // Add userId check
      Promise.all([
        usePortfolioRebalanceStore.getState().fetchAccounts(token, userId), // Pass userId
        usePortfolioRebalanceStore.getState().fetchPMUsers(token),
        usePortfolioRebalanceStore.getState().fetchSnpRatingOptions(token),
        usePortfolioRebalanceStore.getState().fetchMoodyRatingOptions(token),
      ])
        .then(() => {
          if (!cancelled) setInitialLoading(false);
        })
        .catch((err) => {
          // Handle error, set error state, and/or show notification
          console.error("API call failed", err);
          if (!cancelled) setInitialLoading(false); // clear loading state
        });
    }
    return () => {
      cancelled = true;
    };
  }, [token, userId]); // Add userId to dependency array

  // Monitor store errors
  useEffect(() => {
    if (error && error !== lastShownError.current) {
      showError(error);
      lastShownError.current = error;
    }
    if (!error) {
      lastShownError.current = null;
    }
  }, [error, showError]);

  // Fetch table positions when the account or bond type changes.
  // Pagination and sorting are handled client-side.
  useEffect(() => {
    if (selectedAccount && token) {
      setPage(0); // Reset to first page when data source changes
      setIsLoadingPositions(true);

      fetchAccountPositions(selectedAccount, token).finally(() =>
        setIsLoadingPositions(false)
      );
    }
  }, [
    selectedAccount,
    token,
    fetchAccountPositions,
    selectedBondType,
    selectedSecurityCharge,
  ]);

  // Fetch unfiltered positions only when the account changes.
  // This supports validation that must see all holdings, regardless of the active bond type filter.
  useEffect(() => {
    if (selectedAccount && token) {
      fetchAllPositionsForValidation(selectedAccount, token);
    }
  }, [
    selectedAccount,
    token,
    fetchAllPositionsForValidation,
  ]);

  // Client-side sorting
  const sortedPositions = useMemo(() => {
    if (!positions || positions.length === 0) return [];
    if (!sortConfig.key) return positions;

    const sorted = [...positions].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [positions, sortConfig.key, sortConfig.direction]);

  // Client-side pagination
  const paginatedPositions = useMemo(() => {
    const start = page * itemsPerPage;
    return sortedPositions.slice(start, start + itemsPerPage);
  }, [sortedPositions, page, itemsPerPage]);

  // Fetch bond type options on component mount
  useEffect(() => {
    if (token) {
      fetchBondTypeOptions(token);
    }
  }, [token, fetchBondTypeOptions]);

  // Listen for showAccountDeletedModal event
  useEffect(() => {
    const handleShowAccountDeletedModal = (event) => {
      const { deletedAccount, isCurrentlyViewed } = event.detail || {};
      setDeletedAccountInfo({ deletedAccount, isCurrentlyViewed });
      setAccountDeletedModalOpen(true);
    };
    window.addEventListener(
      "showAccountDeletedModal",
      handleShowAccountDeletedModal
    );
    return () => {
      window.removeEventListener(
        "showAccountDeletedModal",
        handleShowAccountDeletedModal
      );
    };
  }, []);

  // Handle CUSIP click
  const handleCusipClick = async (index, rowData) => {
    if (!rowData.cusip && !rowData.security) {
      setSelectedBondDetails({
        error: true,
        message: "CUSIP or Security ID is required.",
      });
      return;
    }
    setBondDetailsLoading(true);
    setBondDetailsOpen(true);
    try {
      const details = await fetchSecurityInfo(
        token,
        rowData.cusip || rowData.security
      );
      setSelectedBondDetails(details);
    } catch (e) {
      setSelectedBondDetails({
        error: true,
        message: e.message || "Failed to fetch bond details.",
      });
    } finally {
      setBondDetailsLoading(false);
    }
  };

  // Handle sort change (client-side only, no API call)
  const handleSortChange = (key, direction) => {
    setSortConfig({ key, direction });
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setPage(0);
  };

  // Handle modal close
  const handleAccountDeletedModalClose = () => {
    setAccountDeletedModalOpen(false);
    setDeletedAccountInfo(null);
  };

  useEffect(() => {
    console.log("positions ", positions);
    return () => {
      // Reset store when component unmounts if needed
      // reset();
    };
  }, [positions]);

  return (
    <>
      <SnackbarComponent />
      {/* Initial loading overlay for all API calls */}
      <Backdrop
        sx={{
          color: tokens.colors.white,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backgroundColor: withAlpha('#000000', 0.7),
          flexDirection: "column",
          gap: 2,
        }}
        open={initialLoading}
      >
        <CircularProgress sx={{ color: tokens.colors.white }} size={60} />
        <Typography variant="h6" sx={{ color: tokens.colors.white }}>
          Loading...
        </Typography>
      </Backdrop>

      {/* Loading overlay for positions */}
      <Backdrop
        sx={{
          color: tokens.colors.white,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: withAlpha('#000000', 0.7),
          flexDirection: "column",
          gap: 2,
        }}
        open={isLoadingPositions && !initialLoading}
      >
        <CircularProgress sx={{ color: tokens.colors.white }} size={60} />
        <Typography variant="h6" sx={{ color: tokens.colors.white }}>
          Loading portfolio positions...
        </Typography>
      </Backdrop>

      <Box
        sx={{
          height: "calc(100vh - 84px)",
          backgroundColor: tokens.colors.bodyBackgroundColor,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: 0,
            "&.MuiContainer-root": {
              paddingLeft: 0,
              paddingRight: 0,
            },
          }}
        >
          {/* Header with portfolio summary (also hosts the inline "results ready"
              affordance when a result arrives while the user is on this page) */}
          <Box sx={{ flexShrink: 0 }}>
            <PortfolioRebalanceHeader />
          </Box>

          {/* Table with positions */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              mt: 0,
            }}
          >
            <PortfolioRebalanceTable
              data={paginatedPositions}
              selectedColumns={selectedColumns}
              allColumns={allColumns}
              loading={false}
              sortConfig={sortConfig}
              onSort={handleSortChange}
              page={page}
              itemsPerPage={itemsPerPage}
              totalElements={sortedPositions.length}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onCusipClick={handleCusipClick}
              // Pass customize columns handler
              onCustomizeColumns={handleCustomizeClick}
            />
          </Box>
        </Container>
      </Box>

      {/* Bond Details Modal */}
      <BondDetailsModal
        open={bondDetailsOpen}
        onClose={() => setBondDetailsOpen(false)}
        bondDetails={selectedBondDetails}
        loading={bondDetailsLoading}
      />
      {/* CustomizeColumns component */}
      <CustomizeColumns
        open={ccOpen}
        onClose={() => setCcOpen(false)}
        allColumns={allColumns}
        selectedColumns={selectedColumns}
        setSelectedColumnsInventory={setSelectedColumns}
        anchorEl={anchorEl}
        sectionKey="portfolio-rebalance"
        height={320}
      />
      {/* Account Deleted Modal */}
      <AccountDeletedModal
        open={accountDeletedModalOpen}
        onClose={handleAccountDeletedModalClose}
        deletedAccount={deletedAccountInfo?.deletedAccount}
        isCurrentlyViewed={deletedAccountInfo?.isCurrentlyViewed}
      />

      {/* Async rebalance non-success result (not achievable / already achieved /
          no trades / error) surfaced after the loader closed */}
      <InformativeModal
        open={!!pendingInfoModal}
        message={pendingInfoModal?.message}
        title={pendingInfoModal?.title}
        onClose={clearPendingInfoModal}
      />
    </>
  );
};

export default PortfolioRebalanceScreen;
